import React from 'react';
import SchoolViewTableSkeleton from './school-views/SchoolViewTableSkeleton';
import SchoolDuplicatesSkeleton from './school-duplicates/SchoolDuplicatesSkeleton';
import InstitutionMembersSkeleton from './school-detail/InstitutionMembersSkeleton';
import SchoolControlSkeleton from './school-control/SchoolControlSkeleton';
import { EmailSendsSkeleton, EmailSendDetailSkeleton } from './email-sends/EmailSendsSkeletons';

type SkeletonType = 'text' | 'table' | 'card' | 'form' | 'filters' | 'stats-grid' | 'dashboard' | 'supervisor-dashboard' | 'schools-table' | 'eval-table' | 'email-table' | 'activity-table' | 'page' | 'school-detail-comms' | 'school-view-table' | 'school-duplicates' | 'institution-members' | 'school-control' | 'email-sends' | 'email-send-detail';

interface SkeletonLoaderProps {
  type?: SkeletonType;
  rows?: number;
  cols?: number;
  width?: string;
  height?: string;
  className?: string;
}

function Bone({ className }: { className: string }) {
  return <div className={`skeleton ${className}`} />;
}

/** 5-row table with header bar */
function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-3">
      {/* Header row */}
      <div className="flex space-x-4">
        {Array.from({ length: cols }).map((_, c) => (
          <Bone key={c} className="h-3 w-1/4" />
        ))}
      </div>
      <div className="skeleton h-px w-full" />
      {/* Data rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex space-x-4">
          {Array.from({ length: cols }).map((_, c) => (
            <Bone key={c} className="h-4 w-1/4" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Card with title + 2–3 lines */
function CardSkeleton() {
  return (
    <div className="card p-6 space-y-4">
      <Bone className="h-6 w-3/4" />
      <Bone className="h-4 w-full" />
      <Bone className="h-4 w-5/6" />
    </div>
  );
}

/** Page-level skeleton for internal routes (header + content blocks) */
function PageSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Bone className="h-8 w-64" />
        <Bone className="h-4 w-80 max-w-full" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="card p-6 space-y-4">
            <Bone className="h-5 w-40" />
            <Bone className="h-4 w-full" />
            <Bone className="h-4 w-5/6" />
            <Bone className="h-4 w-4/6" />
          </div>
        ))}
      </div>

      <div className="card p-6 space-y-4">
        <Bone className="h-5 w-44" />
        {[...Array(6)].map((_, i) => (
          <Bone key={i} className="h-3.5 w-full" />
        ))}
      </div>
    </div>
  );
}

/** Form with labeled input fields */
function FormSkeleton() {
  return (
    <div className="bg-surface rounded-lg shadow border border-border p-6 space-y-5">
      {/* 3 labeled fields */}
      {[{ w: 'w-full' }, { w: 'w-full' }, { w: 'w-full' }].map((field, i) => (
        <div key={i} className="space-y-2">
          <Bone className="h-3 w-20" />
          <Bone className={`h-9 ${field.w} rounded-lg`} />
        </div>
      ))}
      {/* Rich editor area */}
      <div className="space-y-2">
        <Bone className="h-3 w-24" />
        <Bone className="h-10 w-full rounded-t-lg" />
        <Bone className="h-48 w-full rounded-b-lg" />
      </div>
      {/* Footer buttons */}
      <div className="flex justify-end gap-2 pt-4 border-t border-border">
        <Bone className="h-9 w-28 rounded-lg" />
        <Bone className="h-9 w-24 rounded-lg" />
        <Bone className="h-9 w-20 rounded-lg" />
      </div>
    </div>
  );
}

/** Horizontal filter bar (1 row of inputs) */
function FiltersSkeleton({ cols = 4 }: { cols?: number }) {
  return (
    <div className="card p-4">
      <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-4`}>
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Bone className="h-3 w-16" />
            <Bone className="h-9 w-full rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Grid of KPI summary cards */
function StatsGridSkeleton({ cols = 4 }: { cols?: number }) {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-4`}>
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} className="bg-surface rounded-lg shadow p-6 space-y-3">
          <Bone className="h-3 w-20" />
          <Bone className="h-8 w-16" />
          <Bone className="h-3 w-24" />
        </div>
      ))}
    </div>
  );
}

/** High-fidelity dashboard skeleton — mirrors real layout to prevent CLS */
function DashboardSkeleton() {
  return (
    <>
      {/* ── PeriodSelector: icon + "Periodo:" + 4 chip pills ── */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Bone className="h-4 w-4 rounded" />
        <Bone className="h-4 w-14" />
        <Bone className="h-7 w-[72px] rounded-full" />
        <Bone className="h-7 w-16 rounded-full" />
        <Bone className="h-7 w-16 rounded-full" />
        <Bone className="h-7 w-24 rounded-full" />
      </div>

      {/* ── PipelineFunnel ── */}
      <div className="mb-6">
        {/* Tabs: Comercial | Invitaciones */}
        <div className="flex gap-1 mb-3">
          <Bone className="h-7 w-20 rounded-md" />
          <Bone className="h-7 w-24 rounded-md" />
        </div>

        {/* 4 funnel stage cards — horizontal row with chevron gaps */}
        <div className="flex flex-col md:flex-row items-stretch gap-2 mb-4">
          {[...Array(4)].map((_, i) => (
            <React.Fragment key={i}>
              <div className="flex-1 card p-4 flex flex-col items-center justify-center space-y-1.5">
                <Bone className="h-3 w-24" />
                <Bone className="h-9 w-16" />
                <Bone className="h-3 w-20" />
              </div>
              {i < 3 && <div className="hidden md:flex items-center justify-center"><Bone className="h-5 w-5 rounded" /></div>}
            </React.Fragment>
          ))}
        </div>

        {/* Dropoff pills row: 3 status pills */}
        <div className="flex gap-3 mb-4">
          <Bone className="h-7 w-[100px] rounded-full" />
          <Bone className="h-7 w-[88px] rounded-full" />
          <Bone className="h-7 w-20 rounded-full" />
        </div>

        {/* 3 secondary stat cards: icon-box + label + value */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card">
              <div className="card-body flex items-center gap-3">
                <Bone className="h-10 w-10 rounded-lg flex-shrink-0" />
                <div className="space-y-1.5">
                  <Bone className="h-3 w-28" />
                  <Bone className="h-5 w-36" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── AlertRow: 2 full-width alert bars ── */}
      <div className="flex flex-col gap-2 mb-6">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="card rounded-lg px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Bone className="h-4 w-4 rounded flex-shrink-0" />
              <Bone className="h-4 w-64" />
            </div>
            <Bone className="h-4 w-4 rounded flex-shrink-0" />
          </div>
        ))}
      </div>

      {/* ── DashboardSections ── */}
      {/* Section 1: "Actividad del periodo" — expanded with 7 metric cards */}
      <div className="mb-4 bg-surface rounded-lg border border-border overflow-hidden">
        <div className="w-full flex items-center justify-between px-4 py-3">
          <Bone className="h-4 w-36" />
          <Bone className="h-4 w-4 rounded" />
        </div>
        <div className="px-4 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="bg-surface rounded-lg border border-border p-3 space-y-2">
                <Bone className="h-8 w-8 rounded-md" />
                <Bone className="h-3 w-16" />
                <Bone className="h-5 w-10" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section 2: "Ranking de vendedores" — collapsed */}
      <div className="mb-4 bg-surface rounded-lg border border-border px-4 py-3 flex items-center justify-between">
        <Bone className="h-4 w-40" />
        <Bone className="h-4 w-4 rounded" />
      </div>

      {/* Section 3: "Colegios sin seguimiento" — expanded (empty message) */}
      <div className="mb-4 bg-surface rounded-lg border border-border overflow-hidden">
        <div className="w-full flex items-center justify-between px-4 py-3">
          <Bone className="h-4 w-44" />
          <Bone className="h-4 w-4 rounded" />
        </div>
        <div className="px-4 pb-4">
          <Bone className="h-4 w-56 mx-auto" />
        </div>
      </div>

      {/* Section 4: "Métricas de docentes" — collapsed */}
      <div className="mb-4 bg-surface rounded-lg border border-border px-4 py-3 flex items-center justify-between">
        <Bone className="h-4 w-36" />
        <Bone className="h-4 w-4 rounded" />
      </div>

      {/* ── DashboardCharts: collapsed toggle link ── */}
      <div className="mb-6 flex items-center gap-2">
        <Bone className="h-4 w-4 rounded" />
        <Bone className="h-4 w-32" />
      </div>

      {/* ── Quick Access: heading + 3 colored cards ── */}
      <div className="mt-8">
        <Bone className="h-3 w-24 mb-3" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bone className="h-5 w-5 rounded flex-shrink-0" />
                <div className="space-y-1.5">
                  <Bone className="h-4 w-24" />
                  <Bone className="h-3 w-36" />
                </div>
              </div>
              <Bone className="h-4 w-4 rounded flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * Skeleton específico para `SupervisorDashboard.tsx`:
 * - PeriodSelector
 * - 4 KPI cards (hero)
 * - Team title + tabla por miembro (5 filas)
 * - Trend chart placeholder
 */
function SupervisorDashboardSkeleton() {
  return (
    <>
      {/* PeriodSelector */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Bone className="h-4 w-4 rounded" />
        <Bone className="h-4 w-14" />
        <Bone className="h-7 w-12 rounded-full" />
        <Bone className="h-7 w-16 rounded-full" />
        <Bone className="h-7 w-16 rounded-full" />
        <Bone className="h-7 w-16 rounded-full" />
        <Bone className="h-7 w-24 rounded-full" />
      </div>

      {/* Filtros: rol + miembro */}
      <div className="mb-6 rounded-lg border border-border bg-surface p-3 flex flex-wrap items-center gap-3">
        <Bone className="h-3 w-8" />
        <Bone className="h-7 w-28 rounded-md" />
        <Bone className="h-3 w-14" />
        <Bone className="h-7 w-44 rounded-md" />
      </div>

      {/* 4 KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="rounded-lg border border-border bg-surface p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Bone className="h-3 w-20" />
              <Bone className="h-4 w-4 rounded" />
            </div>
            <Bone className="h-9 w-20" />
            <Bone className="h-3 w-32" />
            <Bone className="h-3 w-28" />
          </div>
        ))}
      </div>

      {/* Title + "Ver detalle" link */}
      <div className="flex items-center justify-between mb-3">
        <Bone className="h-5 w-20" />
        <Bone className="h-4 w-24" />
      </div>

      {/* Team table */}
      <div className="bg-surface rounded-lg border border-border overflow-hidden mb-8">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-surface-secondary">
            <tr>
              {['Miembro', 'Activs.', 'Desglose', 'Conv.', 'Interesados', 'Callbacks', 'Cartera', 'Última'].map((h) => (
                <th key={h} className="px-4 py-3 text-left">
                  <Bone className="h-3 w-16" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-border">
            {[...Array(5)].map((_, i) => (
              <tr key={i}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Bone className="h-4 w-24" />
                    <Bone className="h-5 w-20 rounded-full" />
                  </div>
                </td>
                <td className="px-4 py-3 text-right"><Bone className="h-4 w-6 ml-auto" /></td>
                <td className="px-4 py-3 text-right"><Bone className="h-3 w-20 ml-auto" /></td>
                <td className="px-4 py-3 text-right"><Bone className="h-4 w-10 ml-auto" /></td>
                <td className="px-4 py-3 text-right"><Bone className="h-4 w-6 ml-auto" /></td>
                <td className="px-4 py-3 text-right"><Bone className="h-4 w-8 ml-auto" /></td>
                <td className="px-4 py-3 text-right"><Bone className="h-4 w-6 ml-auto" /></td>
                <td className="px-4 py-3 text-right"><Bone className="h-3 w-16 ml-auto" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Chart placeholder */}
      <Bone className="h-5 w-44 mb-3" />
      <div className="rounded-lg border border-border bg-surface p-4">
        <Bone className="h-32 w-full rounded" />
        <div className="flex justify-between mt-2">
          <Bone className="h-3 w-16" />
          <Bone className="h-3 w-16" />
        </div>
      </div>
    </>
  );
}

/** High-fidelity schools table skeleton — matches SchoolsTable row structure */
function SchoolsTableSkeleton({ rows = 8 }: { rows?: number }) {
  // Vary widths per row to look organic
  const nameWidths = ['w-48', 'w-64', 'w-56', 'w-44', 'w-52', 'w-40', 'w-60', 'w-48', 'w-56', 'w-44'];

  return (
    <div className="card">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="w-10 pl-6 py-3"><Bone className="h-4 w-4 rounded" /></th>
              <th className="py-3 px-4"><Bone className="h-3 w-16" /></th>
              <th className="py-3 px-4 w-[140px]"><Bone className="h-3 w-20" /></th>
              <th className="py-3 px-4 w-[120px]"><Bone className="h-3 w-20" /></th>
              <th className="py-3 px-4 w-[100px]"><Bone className="h-3 w-16" /></th>
              <th className="py-3 px-4 w-[160px]"><Bone className="h-3 w-24" /></th>
              <th className="py-3 px-4 w-[120px]"><Bone className="h-3 w-24" /></th>
              <th className="py-3 px-2 w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {Array.from({ length: rows }).map((_, i) => (
              <tr key={i} className={i % 2 === 0 ? '' : 'bg-surface-secondary/30'}>
                {/* Checkbox */}
                <td className="pl-6 py-2.5"><Bone className="h-4 w-4 rounded" /></td>
                {/* Colegio: dot + (name + code badge) / (city, province) */}
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <Bone className="h-2.5 w-2.5 rounded-full flex-shrink-0" />
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Bone className={`h-4 ${nameWidths[i % nameWidths.length]}`} />
                        <Bone className="h-4 w-12 rounded" />
                      </div>
                      <Bone className="h-3 w-32" />
                    </div>
                  </div>
                </td>
                {/* Comercial badge */}
                <td className="px-4 py-2.5"><Bone className="h-5 w-20 rounded-full" /></td>
                {/* Invitación badge */}
                <td className="px-4 py-2.5"><Bone className="h-5 w-20 rounded-full" /></td>
                {/* Docentes (count) */}
                <td className="px-4 py-2.5"><Bone className="h-3.5 w-8" /></td>
                {/* Responsable */}
                <td className="px-4 py-2.5"><Bone className="h-3.5 w-20" /></td>
                {/* Ult. actividad */}
                <td className="px-4 py-2.5"><Bone className="h-3.5 w-16" /></td>
                {/* Chevron */}
                <td className="px-2 py-2.5"><Bone className="h-4 w-4 rounded ml-auto" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** High-fidelity email history table skeleton */
function EmailTableSkeleton({ rows = 8 }: { rows?: number }) {
  const subjectWidths = ['w-56', 'w-72', 'w-52', 'w-64', 'w-60', 'w-56', 'w-72', 'w-52'];
  const metaWidths = ['w-40', 'w-44', 'w-36', 'w-48', 'w-40', 'w-44', 'w-36', 'w-48'];
  const recipientWidths = ['w-40', 'w-48', 'w-36', 'w-44', 'w-52', 'w-40', 'w-48', 'w-36'];

  return (
    <div className="table-container">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <th className="py-3 px-4"><Bone className="h-3 w-14" /></th>
            <th className="py-3 px-4"><Bone className="h-3 w-10" /></th>
            <th className="py-3 px-4 text-center"><Bone className="h-3 w-16 mx-auto" /></th>
            <th className="py-3 px-4 text-center"><Bone className="h-3 w-10 mx-auto" /></th>
            <th className="py-3 px-4"><Bone className="h-3 w-16" /></th>
            <th className="py-3 px-4"><Bone className="h-3 w-20" /></th>
            <th className="py-3 px-4 text-right"><Bone className="h-3 w-16 ml-auto" /></th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i} className={i % 2 === 0 ? '' : 'bg-surface-secondary/30'}>
              <td className="px-4 py-3.5">
                <div className="flex flex-col gap-1">
                  <Bone className={`h-3.5 ${subjectWidths[i % subjectWidths.length]}`} />
                  <Bone className={`h-3 ${metaWidths[i % metaWidths.length]}`} />
                </div>
              </td>
              <td className="px-4 py-3.5"><Bone className={`h-3.5 ${recipientWidths[i % recipientWidths.length]}`} /></td>
              <td className="px-4 py-3.5 text-center">
                <div className="flex flex-col items-center space-y-1">
                  <Bone className="h-4 w-6" />
                  {i % 3 === 0 && <Bone className="h-3 w-20" />}
                </div>
              </td>
              <td className="px-4 py-3.5 text-center">
                <div className="flex flex-col items-center space-y-1">
                  <Bone className="h-4 w-6" />
                  {i % 4 === 0 && <Bone className="h-3 w-20" />}
                </div>
              </td>
              {/* Delivery badge (bloque 04.3) */}
              <td className="px-4 py-3.5"><Bone className="h-5 w-24 rounded-full" /></td>
              {/* Último evento (bloque 04.3) */}
              <td className="px-4 py-3.5"><Bone className="h-3 w-16" /></td>
              <td className="px-4 py-3.5 text-right"><Bone className="h-7 w-7 rounded ml-auto" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Bloque 06.8 — Skeleton del timeline de comunicaciones (rail + 3 items). */
function SchoolDetailCommsSkeleton() {
  return (
    <div className="relative">
      <div className="absolute left-4 top-2 bottom-2 w-px bg-border" aria-hidden />
      <Bone className="h-3 w-20 mb-3 ml-2" />
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="relative pl-10 pb-5">
          <span className="absolute left-3 top-1.5 w-3 h-3 rounded-full bg-gray-200 ring-4 ring-surface" />
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex-1 space-y-1">
              <Bone className="h-3.5 w-56" />
              <Bone className="h-3 w-40" />
            </div>
            <Bone className="h-5 w-24 rounded-full" />
          </div>
          <div className="space-y-1.5 mt-2">
            <Bone className="h-3 w-44" />
            <Bone className="h-3 w-36" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** High-fidelity admin activity table skeleton */
function ActivityTableSkeleton({ rows = 8 }: { rows?: number }) {
  const actionWidths = ['w-48', 'w-56', 'w-40', 'w-52', 'w-44', 'w-60', 'w-48', 'w-36'];

  return (
    <div className="table-container">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <th className="py-3 px-4"><Bone className="h-3 w-14" /></th>
            <th className="py-3 px-4"><Bone className="h-3 w-16" /></th>
            <th className="py-3 px-4"><Bone className="h-3 w-10" /></th>
            <th className="py-3 px-4"><Bone className="h-3 w-14" /></th>
            <th className="py-3 px-4"><Bone className="h-3 w-10" /></th>
            <th className="py-3 px-4 text-right"><Bone className="h-3 w-16 ml-auto" /></th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i} className={i % 2 === 0 ? '' : 'bg-surface-secondary/30'}>
              <td className="px-4 py-3.5"><Bone className="h-3.5 w-28" /></td>
              <td className="px-4 py-3.5">
                <div className="space-y-1.5">
                  <Bone className="h-3.5 w-20" />
                  <Bone className="h-3 w-32" />
                </div>
              </td>
              <td className="px-4 py-3.5"><Bone className="h-5 w-16 rounded-full" /></td>
              <td className="px-4 py-3.5"><Bone className={`h-3.5 ${actionWidths[i % actionWidths.length]}`} /></td>
              <td className="px-4 py-3.5"><Bone className="h-5 w-16 rounded-full" /></td>
              <td className="px-4 py-3.5 text-right"><Bone className="h-7 w-7 rounded ml-auto" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** High-fidelity evaluation requests table skeleton */
function EvalTableSkeleton({ rows = 8 }: { rows?: number }) {
  const schoolWidths = ['w-44', 'w-56', 'w-48', 'w-52', 'w-40', 'w-60', 'w-48', 'w-56'];
  const contactWidths = ['w-28', 'w-36', 'w-32', 'w-24', 'w-36', 'w-28', 'w-32', 'w-24'];

  return (
    <div className="table-container">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <th className="py-3 pl-6 w-10"><Bone className="h-4 w-4 rounded" /></th>
            <th className="py-3 px-4"><Bone className="h-3 w-16" /></th>
            <th className="py-3 px-4"><Bone className="h-3 w-18" /></th>
            <th className="py-3 px-4 hidden sm:table-cell"><Bone className="h-3 w-20" /></th>
            <th className="py-3 px-4"><Bone className="h-3 w-14" /></th>
            <th className="py-3 px-4 text-right"><Bone className="h-3 w-20 ml-auto" /></th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i} className={i % 2 === 0 ? '' : 'bg-surface-secondary/30'}>
              {/* Checkbox */}
              <td className="py-3.5 pl-6">
                <Bone className="h-4 w-4 rounded" />
              </td>
              {/* Colegio: name + code + date */}
              <td className="px-4 py-3.5">
                <div className="space-y-1.5">
                  <Bone className={`h-4 ${schoolWidths[i % schoolWidths.length]}`} />
                  <Bone className="h-3 w-28" />
                  <Bone className="h-3 w-24" />
                </div>
              </td>
              {/* Contacto: name + email + position */}
              <td className="px-4 py-3.5">
                <div className="space-y-1.5">
                  <Bone className={`h-3.5 ${contactWidths[i % contactWidths.length]}`} />
                  <Bone className="h-3 w-36" />
                  <Bone className="h-3 w-20" />
                </div>
              </td>
              {/* Ubicación */}
              <td className="px-4 py-3.5 hidden sm:table-cell">
                <div className="space-y-1.5">
                  <Bone className="h-3.5 w-24" />
                  <Bone className="h-3 w-20" />
                </div>
              </td>
              {/* Estado badge */}
              <td className="px-4 py-3.5">
                <Bone className="h-5 w-20 rounded-full" />
              </td>
              {/* Acciones: 1–3 buttons */}
              <td className="px-4 py-3.5 text-right">
                <div className="flex items-center justify-end space-x-1.5">
                  <Bone className="h-7 w-7 rounded" />
                  {i % 3 !== 2 && (
                    <>
                      <Bone className="h-7 w-[72px] rounded" />
                      <Bone className="h-7 w-[76px] rounded" />
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  type = 'text', rows, cols, width, height, className = '',
}) => {
  switch (type) {
    case 'table':
      return <TableSkeleton rows={rows} cols={cols} />;
    case 'card':
      return <CardSkeleton />;
    case 'form':
      return <FormSkeleton />;
    case 'filters':
      return <FiltersSkeleton cols={cols} />;
    case 'stats-grid':
      return <StatsGridSkeleton cols={cols} />;
    case 'dashboard':
      return <DashboardSkeleton />;
    case 'supervisor-dashboard':
      return <SupervisorDashboardSkeleton />;
    case 'schools-table':
      return <SchoolsTableSkeleton rows={rows} />;
    case 'eval-table':
      return <EvalTableSkeleton rows={rows} />;
    case 'email-table':
      return <EmailTableSkeleton rows={rows} />;
    case 'activity-table':
      return <ActivityTableSkeleton rows={rows} />;
    case 'page':
      return <PageSkeleton />;
    case 'school-detail-comms':
      return <SchoolDetailCommsSkeleton />;
    case 'school-view-table':
      return <SchoolViewTableSkeleton rows={rows} cols={cols} />;
    case 'school-duplicates':
      return <SchoolDuplicatesSkeleton groups={rows} />;
    case 'institution-members':
      return <InstitutionMembersSkeleton rows={rows} />;
    case 'school-control':
      return <SchoolControlSkeleton />;
    case 'email-sends':
      return <EmailSendsSkeleton rows={rows} />;
    case 'email-send-detail':
      return <EmailSendDetailSkeleton />;
    default:
      return (
        <div
          className={`skeleton ${className}`}
          style={{ width: width || '100%', height: height || '1rem' }}
        />
      );
  }
};

export default SkeletonLoader;
